import { Dispatch, SetStateAction } from "react";
import { t } from "i18next";

import {
  GlossariesUpdateGlossaryMetadataPostRequest,
  GlossariesUpsertGlossaryPostRequest,
} from "../../../../../../@api";

import { IGlossaryFileSupportExtended } from "../../../../../features/translation/TranslationHeader/GlossarySelection/GlossarySelection.types";
import { translationKeys } from "../../../../../models/app-translation-keys";
import {
  ISharedDialogAction,
  ISharedDialogActionContext,
  sharedDialog,
} from "../../../../Shared/Components/Dialogs/SharedDialog";
import { IManageGlossaryDialogState } from "./manageGlossaryDialogTypes";

interface IManageGlossaryDialogFooterActionsProps {
  updateGlossaryMetadata: (request: GlossariesUpdateGlossaryMetadataPostRequest) => Promise<void>;
  uploadGlossaryFile: (request: GlossariesUpsertGlossaryPostRequest) => Promise<string>;
  glossary?: IGlossaryFileSupportExtended;
  onHide(success?: boolean): void;
  isEdit: boolean;
}

const glossaryTranslationKeys = translationKeys.adminArea.glossary;

const getFormStatus = (state: IManageGlossaryDialogState) => {
  const isDisabled = state.isDisabled;
  const tooltip = state.isUploading ? t(glossaryTranslationKeys.dialog.uploadInProgress) : undefined;

  return {
    isDisabled,
    tooltip,
  };
};

// set uploading state
const setUploading = (setState: Dispatch<SetStateAction<IManageGlossaryDialogState>>, isUploading: boolean) => {
  setState(prev => ({
    ...prev,
    isUploading,
    isDisabled: isUploading ? true : prev.glossaryName.length < 3,
  }));
};

export const manageGlossaryDialogFooterActions = ({
  updateGlossaryMetadata,
  uploadGlossaryFile,
  glossary,
  onHide,
  isEdit,
}: IManageGlossaryDialogFooterActionsProps) => {
  return ({ id, sharedState, setSharedDialogState }: ISharedDialogActionContext): ISharedDialogAction[] => {
    const state = sharedState as IManageGlossaryDialogState;
    const setState = setSharedDialogState as Dispatch<SetStateAction<IManageGlossaryDialogState>>;
    const formStatus = getFormStatus(state);

    const handleCancel = () => {
      if (id) {
        sharedDialog.closeSharedDialog(id);
      }
      onHide();
    };

    const handleSubmit = async () => {
      setUploading(setState, true);

      try {
        await submitGlossary(state, glossary);
      } catch {
        setState(previousState => ({
          ...previousState,
          isUploading: false,
          isDisabled: previousState.glossaryName.length < 3,
        }));
      } finally {
        setUploading(setState, false);

        if (id) {
          sharedDialog.closeSharedDialog(id);
        }

        onHide(true);
      }
    };

    const submitGlossary = async (state: IManageGlossaryDialogState, glossary?: IGlossaryFileSupportExtended) => {
      if (!state.glossaryFile) {
        return await updateGlossaryMetadata({
          glossaryId: glossary!.glossaryId,
          glossaryName: encodeURIComponent(state.glossaryName),
          glossaryDescription: encodeURIComponent(state.glossaryDescription),
        });
      }

      return await uploadGlossaryFile({
        glossaryId: glossary?.glossaryId,
        glossaryName: encodeURIComponent(state.glossaryName),
        glossaryDescription: encodeURIComponent(state.glossaryDescription),
        uploadedFile: state.glossaryFile.file,
      });
    };

    return [
      {
        label: t(translationKeys.actions.cancel),
        isTextButton: true,
        isDisabled: false,
        isLoading: false,
        tooltip: undefined,
        tooltipOptions: undefined,
        onClick: handleCancel,
      },

      {
        label: t(
          isEdit ? glossaryTranslationKeys.actions.updateGlossary : glossaryTranslationKeys.actions.createGlossary,
        ),
        isTextButton: false,
        isDisabled: formStatus.isDisabled,
        isLoading: state.isUploading,
        tooltip: formStatus.tooltip,
        tooltipOptions: {
          position: "top",
          showOnDisabled: true,
          showDelay: 200,
        },
        onClick: handleSubmit,
      },
    ];
  };
};
